import React, { useEffect, useRef } from 'react';
import Chart, { Plugin } from 'chart.js/auto';
import { DecompositionChartData } from './types';
import { floatingBarLabelsPlugin, lineToNextBarPlugin } from './plugins';
import { useMobile } from '../../context/mobile/mobile.provider';
import classNames from 'classnames';

export type Props = {
  info: DecompositionChartData[];
  chartFullscreen?: boolean;
};

function DecompositionChart({ info, chartFullscreen }: Props) {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const chartRef = useRef<Chart | null>(null);
  const infoRef = useRef<string>(null);
  const { isMobile } = useMobile();

  useEffect(() => {
    if (!canvasRef.current) {
      return;
    }

    if (!canvasRef.current || !info || infoRef.current === JSON.stringify(info)) {
      return;
    }

    infoRef.current = JSON.stringify(info);

    if (chartRef.current) {
      chartRef.current.destroy();
      chartRef.current = null;
    }

    const plugins: Plugin[] = [lineToNextBarPlugin(), floatingBarLabelsPlugin(info.map(item => item.floatingLabel))];

    chartRef.current = new Chart(canvasRef.current, {
      plugins: plugins,
      type: 'bar',
      data: {
        labels: info.map(item => item.xLabel),
        datasets: [
          {
            data: info.map(item => item.data),
            backgroundColor: info.map(item => item.fillColor),
            hoverBackgroundColor: info.map(item => item.fillColor),
            barPercentage: 0.8,
            categoryPercentage: 1,
            minBarLength: 1,
          },
        ],
      },
      options: {
        animation: false,
        layout: { padding: { top: 40, bottom: 40 } }, // to fit floating labels
        responsive: isMobile ? false : true,
        maintainAspectRatio: false,
        plugins: {
          legend: {
            display: false,
          },
          tooltip: null,
        },
        scales: {
          y: {
            display: false,
          },
          x: {
            grid: {
              display: false,
            },
            border: {
              display: false,
            },
          },
        },
      },
    });
  }, [info]);

  return (
    <>
      {isMobile ? (
        <div style={{ maxHeight: chartFullscreen ? '70vh' : '330px', maxWidth: '90vw', overflow: 'auto' }}>
          <canvas
            className={classNames({ 'h-330': !chartFullscreen, 'h-70vw': chartFullscreen })}
            width="1100px"
            ref={canvasRef}
          />
        </div>
      ) : (
        <div style={{ maxHeight: '330px' }}>
          <canvas className="h-full w-full" ref={canvasRef} />
        </div>
      )}
    </>
  );
}
export { DecompositionChart };
